import { Grid, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../app/hooks";
import CommonTextField from "../../../../common/components/functional/commonTextField/CommonTextField";
import FormSection from "../../../../common/components/functional/formSection/FormSection";
import isEmpty from "../../../../common/helpers/isEmpty";
import LabelCustomInputRow from "../../../customer/contract/form/LabelCustomInputRow";
import { Employment } from "../../../employment/employmentApi";
import { editEmpOtherAgreedInformation } from "../../../employment/employmentSlice";

type Props = {
  employment?: Employment;
  canEdit: boolean;
};

const OtherAgreedInformation = ({ employment, canEdit }: Props) => {
  const [edit, setEdit] = useState(false);

  const { t } = useTranslation("talentProfile");
  const dispatch = useAppDispatch();

  const defaultValues = useMemo(
    () => ({
      otherAgreedInformation: employment?.details?.otherAgreedInformation || "",
    }),
    [employment?.details?.otherAgreedInformation],
  );

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [employment, methods, defaultValues]);

  if (employment?.isShift) return null;

  const {
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = methods;

  const onSubmit = (data: FieldValues) => {
    const payload = {
      employmentId: employment?.employmentId as string,
      version: employment?.details.version as number,
      otherAgreedInformation: data.otherAgreedInformation,
    };

    dispatch(editEmpOtherAgreedInformation(payload)).then((action) => {
      if (isFulfilled(action)) {
        setEdit(false);
      }
    });
  };

  const onSave = () => {
    if (!edit) setEdit(true);
    if (edit) handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    setEdit(false);
    // TODO: check reset after backend implemented
    reset({
      otherAgreedInformation: employment?.details.otherAgreedInformation,
    });
  };

  const saveDisabled = !isDirty || !isEmpty(errors);

  return (
    <FormSection
      title={t("otherAgreedInfo")}
      edit={edit}
      saveDisabled={saveDisabled}
      onEdit={onSave}
      onCancel={onCancel}
      readOnly={!canEdit}
    >
      <Grid
        item
        container
        xs={2}
        justifyContent="flex-end"
        alignItems="start"
        mt="8px"
      >
        <Typography textAlign="end" variant="secondary">
          {t("message")}
        </Typography>
      </Grid>
      <Grid item xs={10}>
        <FormProvider {...methods}>
          <LabelCustomInputRow
            fieldName="otherAgreedInformation"
            inputWidth={12}
            render={(field) => (
              <CommonTextField
                {...field}
                disabled={!edit}
                multiline
                minRows={4}
                maxRows={4}
                size="small"
                fullWidth
                sx={{ maxWidth: "526px" }}
              />
            )}
          />
        </FormProvider>
      </Grid>
    </FormSection>
  );
};

export default OtherAgreedInformation;
